import { EnvironmentObject } from '../../data/common/EnvironmentObject';
import { LayerBitMask } from '../layers/Layer';
import { PlaneView } from '../views/PlaneView';
import { ViewUpdateEvent } from './ViewUpdateEvent';
import { RotatedBoundingBox } from '~/engine/data/common/RotatedBoundingBox';

/**
 * {@link VolumeViewUpdateEvent} is a {@link ViewUpdateEvent} that updates {@link View Views} if their viewport
 * intersects with a 3D {@link volume} cuboid.
 */
export class VolumeViewUpdateEvent extends ViewUpdateEvent {
    /**
     * {@link BoundingBox} that represents a 3D volume in which an event occured.
     */
    private readonly volume?: RotatedBoundingBox;

    public constructor(
        volume: EnvironmentObject | RotatedBoundingBox | undefined,
        affectedLayers: LayerBitMask,
        updateContent = true
    ) {
        super(affectedLayers, updateContent);
        if (
            volume instanceof EnvironmentObject &&
            volume.boundingBox.min &&
            volume.boundingBox.max
        ) {
            this.volume = volume.boundingBox;
        } else if (volume instanceof RotatedBoundingBox) {
            this.volume = volume;
        }
    }

    /**
     * Method to check if this {@link VolumeViewUpdateEvent} should update a specific {@link View}.\
     * Generally speaking this should return true if the View's viewport covers even a part of the 3D volume.
     * @param view A {@link View} that might be updated
     * @returns True if the {@link view} shall be updated. False otherwise.
     */
    public shouldUpdateView(view: PlaneView): boolean {
        if (!this.volume) {
            return true; // If no volume is defined, update all views
        }

        // if the bounding box is not well constructed we should update the view (not all code can handle inverted orientations)
        if (
            !this.volume.min ||
            !this.volume.max ||
            this.volume.min[0] > this.volume.max[0] ||
            this.volume.min[1] > this.volume.max[1] ||
            this.volume.min[2] > this.volume.max[2]
        ) {
            return true;
        }

        const intersections = this.volume.intersections(
            view.projectionViewMatrix
        );
        return intersections.length > 0;
    }
}
